import React, { useEffect, useRef, useState, useContext } from 'react';
import SimplePeer from 'simple-peer';
import { SocketContext, PlayerDataContext } from '../../App';

// Mesh voice chat among players in the same room using WebRTC (audio only by default)
const VoiceChat = ({ video = false, onClose, position = 'bottom-right', muted: externalMuted }) => {
  const socket = useContext(SocketContext);
  const player = useContext(PlayerDataContext);
  const localStreamRef = useRef(null);
  const peersRef = useRef(new Map()); // peerId -> SimplePeer
  const [peers, setPeers] = useState([]); // for rendering remote media
  // externalMuted controls the mic from the parent button; internal muted is derived from it
  const muted = externalMuted !== undefined ? externalMuted : false;

  const roomId = player?.roomId;

  useEffect(() => {
    if (!socket || !roomId) return undefined;
    let mounted = true;
    const peersMap = peersRef.current;
    const rejoinRTC = () => {
      socket.emit('rtc:join', { roomId });
    };

    const start = async () => {
      try {
        const constraints = video ? { video: true, audio: true } : { audio: true };
        const media = await navigator.mediaDevices.getUserMedia(constraints);
        if (!mounted) return;
        localStreamRef.current = media;

        // Join RTC signaling for this room
        socket.emit('rtc:join', { roomId });

        socket.on('connect', rejoinRTC);

        // Receive current peers and create outbound offers
        socket.on('rtc:peers', ({ peers }) => {
          peers.forEach(peerId => createPeer(peerId, true));
        });

        // Let the newcomer initiate offers to existing peers to avoid double-offer glare.
        socket.on('rtc:peer-joined', ({ peerId }) => {
          if (peerId && peerId !== socket.id && !peersMap.has(peerId)) {
            // wait for their offer; create the responder peer lazily on incoming signal
          }
        });

        // Peer left, clean up
        socket.on('rtc:peer-left', ({ peerId }) => {
          const peer = peersMap.get(peerId);
          if (peer) {
            peer.destroy();
            peersMap.delete(peerId);
            setPeers(prev => prev.filter(p => p.id !== peerId));
          }
        });

        // Incoming signal (answer or offer)
        socket.on('rtc:signal', ({ from, data }) => {
          let peer = peersMap.get(from);
          if (!peer) {
            // Create a peer in non-initiator mode for incoming offer
            peer = createPeer(from, false);
          }
          peer.signal(data);
        });

        function createPeer(peerId, initiator) {
          if (peersMap.has(peerId)) return peersMap.get(peerId);
          const p = new SimplePeer({
            initiator,
            trickle: true,
            stream: localStreamRef.current,
          });

          p.on('signal', data => {
            socket.emit('rtc:signal', { targetId: peerId, data });
          });

          p.on('stream', remoteStream => {
            setPeers(prev => {
              // avoid duplicates
              if (prev.some(x => x.id === peerId)) return prev;
              return [...prev, { id: peerId, stream: remoteStream }];
            });
          });

          p.on('close', () => {
            peersMap.delete(peerId);
            setPeers(prev => prev.filter(p => p.id !== peerId));
          });

          p.on('error', () => {
            // graceful degrade
          });

          peersMap.set(peerId, p);
          return p;
        }
      } catch (e) {
        console.error('VoiceChat init failed', e);
        onClose?.();
      }
    };

    start();

    return () => {
      mounted = false;
      try { socket.emit('rtc:leave', { roomId }); } catch {}
      socket.off('rtc:peers');
      socket.off('rtc:peer-joined');
      socket.off('rtc:peer-left');
      socket.off('rtc:signal');
      socket.off('connect', rejoinRTC);
      peersMap.forEach(p => p.destroy());
      peersMap.clear();
      setPeers([]);
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(t => t.stop());
        localStreamRef.current = null;
      }
    };
  }, [socket, roomId, video, onClose]);

  useEffect(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    stream.getAudioTracks().forEach(t => (t.enabled = !muted));
  }, [muted]);

  // Audio-only: no visible UI panel needed — mic toggle is in the topbar
  if (!video) {
    return (
      <>
        {peers.map(p => (
          <audio
            key={p.id}
            autoPlay
            playsInline
            ref={el => {
              if (!el) return;
              if (el.srcObject !== p.stream) el.srcObject = p.stream;
              el.play?.().catch(() => {});
            }}
          />
        ))}
      </>
    );
  }

  const basePos = position === 'bottom-left' ? { left: 16 } : { right: 16 };
  return (
    <div style={{position:'fixed',bottom:16,...basePos,zIndex:1000,background:'rgba(0,0,0,0.7)',color:'#fff',padding:12,borderRadius:10,minWidth:220}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <strong>Video Chat</strong>
        <button onClick={onClose} style={{background:'transparent',border:'none',color:'#fff',cursor:'pointer',width:'auto',height:'auto',boxShadow:'none',padding:'2px 6px'}}>✕</button>
      </div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        {localStreamRef.current && (
          <video style={{width:100,height:70,background:'#222'}} muted autoPlay playsInline ref={el => { if (el && localStreamRef.current) el.srcObject = localStreamRef.current; }} />
        )}
        {peers.map(p => (
          <video key={p.id} style={{width:100,height:70,background:'#222'}} autoPlay playsInline ref={el => { if (el) el.srcObject = p.stream; }} />
        ))}
      </div>
    </div>
  );
};

export default VoiceChat;
